import { useCallback, useEffect, useRef, useState } from "react";
import { updateJobs } from "../../../config/Services";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import { performPendingKitchenDecision } from "./pendingKitchenDecisions";
import { REJECTION_REASON_OPTIONS, validateRejectionReason } from "./rejectionReasons";

export default function useKitchenPendingActions({
  getFreshJobs, onUpdated, onAcknowledged, orgId, locationId,
  updatedBy, updatedByName, allowAccept = true, allowReject = true,
}) {
  const showSnackbar = useSnackbar();
  const scope = JSON.stringify([orgId, locationId]);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const callbacks = useRef({ onUpdated, onAcknowledged });
  callbacks.current = { onUpdated, onAcknowledged };
  const writeRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [targetJobIds, setTargetJobIds] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  useEffect(() => {
    setTargetJobIds([]);
    setSelectedReason("");
    setCustomReason("");
    setReasonError("");
  }, [scope]);

  const run = useCallback(async (jobIds, status, reason) => {
    if (writeRef.current) throw new Error("A kitchen decision is already in progress.");
    if ((status === "IN_PROGRESS" && !allowAccept) || (status === "REJECTED" && !allowReject)) {
      throw new Error(status === "REJECTED" ? "Reject orders is disabled." : "Accept orders is disabled.");
    }
    writeRef.current = true;
    setBusy(true);
    try {
      const result = await performPendingKitchenDecision({
        jobIds, status, reason, getFreshJobs, updateJobs,
        audit: { locationId, updatedBy, updatedByName },
        isCurrent: () => scopeRef.current === scope,
      });
      // A write can finish after a location switch. Never update the new kitchen.
      if (scopeRef.current !== scope) return { ...result, staleScope: true };
      callbacks.current.onAcknowledged?.(result.jobIds);
      await callbacks.current.onUpdated?.(result);
      showSnackbar(status === "REJECTED" ? "Orders rejected" : "Orders accepted and moved to processing", "success");
      return result;
    } catch (error) {
      if (scopeRef.current === scope) {
        if (status === "REJECTED") setReasonError(error.message || "Could not reject these orders.");
        showSnackbar(error.message || "Kitchen update failed", "error");
      }
      throw error;
    } finally {
      writeRef.current = false;
      setBusy(false);
    }
  }, [getFreshJobs, scope, locationId, updatedBy, updatedByName, allowAccept, allowReject, showSnackbar]);

  const accept = useCallback((jobIds) => run(jobIds, "IN_PROGRESS"), [run]);
  const requestReject = useCallback((jobIds) => {
    if (writeRef.current || !allowReject) return;
    const ids = [...new Set((jobIds || []).filter(Boolean).map(String))];
    if (!ids.length) return;
    setTargetJobIds(ids);
    setSelectedReason("");
    setCustomReason("");
    setReasonError("");
  }, [allowReject]);
  const cancelReject = useCallback(() => {
    if (!writeRef.current) { setTargetJobIds([]); setReasonError(""); }
  }, []);
  const validation = validateRejectionReason(selectedReason, customReason);
  const confirmReject = useCallback(async () => {
    if (writeRef.current) return;
    const checked = validateRejectionReason(selectedReason, customReason);
    if (checked.error) { setReasonError(checked.error); return; }
    try {
      const result = await run(targetJobIds, "REJECTED", checked.reason);
      if (!result.staleScope) setTargetJobIds([]);
      return result;
    } catch (_) {
      // Keep the reason and selection for retry; run reports the actual error.
    }
  }, [run, targetJobIds, selectedReason, customReason]);

  return {
    accept, requestReject, confirmReject, cancelReject, busy, targetJobIds,
    selectedReason, customReason, setSelectedReason, setCustomReason,
    reasonOptions: REJECTION_REASON_OPTIONS, reasonError,
    isReasonValid: !validation.error && targetJobIds.length > 0,
  };
}
